import React, {FC, useState} from 'react';
import {ALIGN, Button, FLEX_DIRECTION, Link, SPACE, useDevice, View} from '@lookiero/aurora';
import {useIntl} from 'react-intl';
import {useReferralLink} from '@raf/ui/hooks/useReferralLink';
import {Section} from '@views/Referrals';
import {ShareModal} from '@views/Referrals/_components/ShareModal/ShareModal';
import {useWebview} from '@app/components/Webview';
import {style} from './ShareButtons.styles';
import {TEXT, TERMS_PUBLIC} from './ShareButtons.definition';

export enum ShareButtonsVariant {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

interface ShareButtonsProps {
  onSharePress?: () => void;
  referralCode?: string;
  referredCredit: number;
  section: Section;
  showMoreInfoLink?: boolean;
  variant?: ShareButtonsVariant;
  testID?: string;
}

export const ShareButtons: FC<ShareButtonsProps> = ({
  onSharePress,
  referralCode = '',
  referredCredit,
  section,
  showMoreInfoLink = false,
  variant = ShareButtonsVariant.VERTICAL,
  testID,
}) => {
  const {formatMessage} = useIntl();
  const {shareReferral} = useReferralLink({referralCode, referredCredit});
  const {openLink} = useWebview();
  const {platform, screen} = useDevice();

  const [shareModalVisible, setShareModalVisible] = useState(false);

  const copyLink = (): void => void shareReferral('clipboard', Section.REFERRAL_VIEW);
  const goToHowItWorks = platform.native ? () => openLink(TERMS_PUBLIC) : undefined;
  const horizontal = variant === ShareButtonsVariant.HORIZONTAL;
  const testIdPrefix = testID ? `${testID}_` : '';

  return (
    <View flexDirection={horizontal ? FLEX_DIRECTION.ROW : FLEX_DIRECTION.COLUMN} style={style.flex1}>
      <Button
        icon="copy"
        testID={testIdPrefix + 'share-button-copy'}
        onPress={copyLink}
        style={horizontal && style.flex1}
        small={!screen.L}
      >
        {formatMessage(TEXT.COPY_LINK_CTA)}
      </Button>
      {horizontal && <View style={style.separationView} />}
      <Button
        icon={'share'}
        onPress={() => {
          if (onSharePress) onSharePress();
          setShareModalVisible(true);
        }}
        marginTop={horizontal ? undefined : SPACE.SPACE_4}
        style={horizontal && style.flex1}
        small={!screen.L}
        testID={testIdPrefix + 'open-share-cta'}
      >
        {formatMessage(TEXT.SHARE_BY_SEND)}
      </Button>
      {showMoreInfoLink && (
        <Link
          marginTop={screen.L ? SPACE.SPACE_4 : SPACE.SPACE_6}
          href={TERMS_PUBLIC}
          align={ALIGN.CENTER}
          target="_blank"
          underlined
          onPress={goToHowItWorks}
          testID={testIdPrefix + 'show-info-link'}
        >
          {formatMessage(TEXT.MORE_INFO_LINK)}
        </Link>
      )}
      <ShareModal
        isVisible={shareModalVisible}
        onClose={() => setShareModalVisible(false)}
        referralCode={referralCode}
        referredCredit={referredCredit}
        section={section}
      />
    </View>
  );
};
